import { useEffect, useState, useCallback } from 'react';
import { getSubscription, cancelSubscription, createSubscription } from '@genfun/core/api/auth';
import { openTabWithUrl } from '@genfun/core/utils/openTabWithUrl';
import './AccountPopup.css';

// Tier metadata — kept in sync with the Landing/PricingPopup plans. All plans
// are recurring (billed weekly).
const TIER_META = {
    1: {
        name: 'Starter',
        price: '$9.99',
        period: '/w',
        features: ['50 photo generations', '10 video generations', 'No watermark', 'HD quality'],
    },
    2: {
        name: 'Premium',
        price: '$19.99',
        period: '/w',
        features: ['400 photo generations', '20 video generations', 'Priority queue', 'Full HD quality'],
    },
};

const PLAN_CARDS = [
    {
        tier: 1,
        name: 'Starter',
        price: '$9.99',
        period: '/w',
        features: ['50 photo generations', '10 video generations', 'No watermark', 'Cancel anytime'],
    },
    {
        tier: 2,
        name: 'Premium',
        price: '$19.99',
        strike: '$60',
        period: '/w',
        popular: true,
        features: ['400 photo generations', '20 video generations', 'Priority queue', 'Cancel anytime'],
    },
];

const STATUS_META = {
    active:    { label: 'Active',    tone: 'good' },
    cancelled: { label: 'Cancelled', tone: 'warn' },
    past_due:  { label: 'Past due',  tone: 'bad'  },
};

function formatDate(iso) {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// Subscriptions are weekly (SUB_CYCLE_DAYS=7 on the backend). We don't get an explicit
// next-billing date from the backend, so we derive it as last_rebill_at + 7 days (or started_at + 7 days
// if there hasn't been a rebill yet). Marked as estimated in the UI.
function computeNextBilling(sub) {
    if (!sub) return null;
    const anchor = sub.last_rebill_at || sub.started_at;
    if (!anchor) return null;
    const d = new Date(anchor);
    if (isNaN(d.getTime())) return null;
    d.setDate(d.getDate() + 7);
    return d;
}

function daysUntil(date) {
    if (!date) return null;
    const ms = date.getTime() - Date.now();
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

export function AccountPopup({ isOpen, onClose, token, user, refreshUser }) {
    const [sub, setSub] = useState(null);
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [confirmCancel, setConfirmCancel] = useState(false);
    const [subscribingTier, setSubscribingTier] = useState(null);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        if (!token) { setSub(null); setStatus(null); return; }
        setLoading(true);
        setError(null);
        try {
            const [data, code] = await getSubscription(token);
            setSub(data);
            setStatus(code);
            if (code !== 200 && code !== 404) {
                setError('Could not load subscription — please try again later.');
            }
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (!isOpen) return;
        document.body.style.overflow = 'hidden';
        refresh();
        return () => { document.body.style.overflow = ''; };
    }, [isOpen, refresh]);

    const handleSubscribe = (tier) => {
        if (!token || subscribingTier !== null) return;
        setSubscribingTier(tier);
        setError(null);
        openTabWithUrl(() => createSubscription(token, tier).then(r => r.url))
            .catch(e => setError(e.message || String(e)))
            .finally(() => setSubscribingTier(null));
    };

    const handleCancel = async () => {
        if (!confirmCancel) { setConfirmCancel(true); return; }
        setCancelling(true);
        setError(null);
        try {
            const [data, code] = await cancelSubscription(token);
            if (code !== 200) {
                setError((data && data.detail) || 'Failed to cancel');
                return;
            }
            setConfirmCancel(false);
            await refresh();
            refreshUser?.();
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setCancelling(false);
        }
    };

    if (!isOpen) return null;

    const hasSub      = status === 200 && sub;
    const isActive    = hasSub && sub.status === 'active';
    const tier        = hasSub ? TIER_META[sub.tier] || { name: `Tier ${sub.tier}`, price: '', period: '', features: [] } : null;
    const statusMeta  = hasSub ? (STATUS_META[sub.status] || { label: sub.status, tone: 'warn' }) : null;
    const isCancelled = hasSub && sub.status === 'cancelled';
    const nextBilling = hasSub && !isCancelled ? computeNextBilling(sub) : null;
    const accessUntil = hasSub && isCancelled ? computeNextBilling(sub) : null;
    const credits     = user?.credits ?? 0;

    return (
        <div className="acctOverlay" onClick={onClose}>
            <div className="acctPopup" onClick={e => e.stopPropagation()}>
                <button className="acctClose" onClick={onClose} aria-label="Close">&times;</button>

                <div className="acctHeader">
                    <div className="acctAvatar">{(user?.email || '?').slice(0, 1).toUpperCase()}</div>
                    <div className="acctIdentity">
                        <div className="acctName">My Account</div>
                        <div className="acctEmail">{user?.email || '—'}</div>
                    </div>
                </div>

                {hasSub && (
                    <div className="acctCreditsCard">
                        <div className="acctCreditsLabel">Credits available</div>
                        <div className="acctCreditsValue">
                            <span className="acctCreditsNum">{credits}</span>
                            <span className="acctCreditsUnit">credits</span>
                        </div>
                        {tier && (
                            <div className="acctCreditsHint">
                                {isActive
                                    ? `Refills with your ${tier.name} plan`
                                    : accessUntil
                                        ? `Use them by ${formatDate(accessUntil.toISOString())}`
                                        : 'Use them before your access ends'}
                            </div>
                        )}
                    </div>
                )}

                <div className="acctSectionTitle">Subscription</div>

                {loading && !sub && (
                    <div className="acctLoading">Loading…</div>
                )}

                {!loading && status === 404 && (
                    <div className="acctPlans">
                        <div className="acctPlansIntro">Pick a plan — cancel anytime.</div>
                        <div className="acctPlanCards">
                            {PLAN_CARDS.map(p => (
                                <div key={p.tier} className={`acctPlan${p.popular ? ' acctPlan--popular' : ''}`}>
                                    {p.popular && <div className="acctPlanBadge">Best value</div>}
                                    <div className="acctPlanName">{p.name}</div>
                                    <div className="acctPlanPrice">
                                        {p.price}
                                        <span className="acctPlanPer">{p.period}</span>
                                        {p.strike && <s>{p.strike}</s>}
                                    </div>
                                    <ul className="acctPlanFeatures">
                                        {p.features.map((f, i) => <li key={i}>{f}</li>)}
                                    </ul>
                                    <button
                                        className={`acctPlanBtn${p.popular ? ' acctPlanBtn--primary' : ''}`}
                                        onClick={() => handleSubscribe(p.tier)}
                                        disabled={subscribingTier !== null}
                                    >
                                        {subscribingTier === p.tier ? 'Redirecting…' : 'Subscribe'}
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div className="acctPlansFoot">🔒 Secure checkout · Cancel anytime</div>
                    </div>
                )}

                {!loading && hasSub && (
                    <div className="acctSubCard">
                        <div className="acctSubTop">
                            <div>
                                <div className="acctSubPlan">{tier.name}</div>
                                <div className="acctSubPrice">
                                    {tier.price}<span className="acctSubPeriod">{tier.period}</span>
                                </div>
                            </div>
                            <span className={`acctStatus acctStatus--${statusMeta.tone}`}>
                                <span className="acctStatusDot" /> {statusMeta.label}
                            </span>
                        </div>

                        <div className="acctSubRows">
                            {sub.started_at && (
                                <div className="acctSubRow">
                                    <span className="acctSubRowLabel">Started</span>
                                    <span className="acctSubRowVal">{formatDate(sub.started_at)}</span>
                                </div>
                            )}
                            {sub.last_rebill_at && (
                                <div className="acctSubRow">
                                    <span className="acctSubRowLabel">Last payment</span>
                                    <span className="acctSubRowVal">{formatDate(sub.last_rebill_at)}</span>
                                </div>
                            )}
                            {nextBilling && (
                                <div className="acctSubRow acctSubRow--accent">
                                    <span className="acctSubRowLabel">Next renewal</span>
                                    <span className="acctSubRowVal">
                                        {formatDate(nextBilling.toISOString())}
                                        {daysUntil(nextBilling) !== null && daysUntil(nextBilling) >= 0 && (
                                            <span className="acctSubRowMeta"> · in {daysUntil(nextBilling)} day{daysUntil(nextBilling) === 1 ? '' : 's'}</span>
                                        )}
                                    </span>
                                </div>
                            )}
                            {isCancelled && accessUntil && (
                                <div className="acctSubRow acctSubRow--accent">
                                    <span className="acctSubRowLabel">Access until</span>
                                    <span className="acctSubRowVal">{formatDate(accessUntil.toISOString())}</span>
                                </div>
                            )}
                            {sub.cancelled_at && (
                                <div className="acctSubRow">
                                    <span className="acctSubRowLabel">Cancelled on</span>
                                    <span className="acctSubRowVal">{formatDate(sub.cancelled_at)}</span>
                                </div>
                            )}
                        </div>

                        {tier.features?.length > 0 && (
                            <ul className="acctFeatures">
                                {tier.features.map((f, i) => <li key={i}>{f}</li>)}
                            </ul>
                        )}

                        {isCancelled ? (
                            <div className="acctRenew">
                                <button
                                    className="acctRenewBtn"
                                    onClick={() => handleSubscribe(sub.tier)}
                                    disabled={subscribingTier !== null}
                                >
                                    {subscribingTier === sub.tier
                                        ? 'Redirecting…'
                                        : `Renew ${tier.name} — ${tier.price}${tier.period}`}
                                </button>
                                <div className="acctRenewNote">
                                    You keep access until {accessUntil ? formatDate(accessUntil.toISOString()) : 'the period ends'}.
                                </div>
                            </div>
                        ) : (
                            <div className="acctActions">
                                <button
                                    className={`acctCancel${confirmCancel ? ' acctCancel--confirm' : ''}`}
                                    onClick={handleCancel}
                                    disabled={cancelling}
                                >
                                    {cancelling
                                        ? 'Cancelling…'
                                        : confirmCancel
                                            ? 'Click again to confirm cancel'
                                            : 'Cancel subscription'}
                                </button>
                                {confirmCancel && !cancelling && (
                                    <button className="acctCancelBack" onClick={() => setConfirmCancel(false)}>
                                        Keep subscription
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {!loading && hasSub && isCancelled && (
                    <div className="acctPlans acctPlans--inline">
                        <div className="acctPlansDivider"><span>or switch plan</span></div>
                        <div className="acctPlanCards">
                            {PLAN_CARDS.filter(p => p.tier !== sub.tier).map(p => (
                                <div key={p.tier} className={`acctPlan${p.popular ? ' acctPlan--popular' : ''}`}>
                                    {p.popular && <div className="acctPlanBadge">Best value</div>}
                                    <div className="acctPlanName">{p.name}</div>
                                    <div className="acctPlanPrice">
                                        {p.price}
                                        <span className="acctPlanPer">{p.period}</span>
                                        {p.strike && <s>{p.strike}</s>}
                                    </div>
                                    <ul className="acctPlanFeatures">
                                        {p.features.map((f, i) => <li key={i}>{f}</li>)}
                                    </ul>
                                    <button
                                        className={`acctPlanBtn${p.popular ? ' acctPlanBtn--primary' : ''}`}
                                        onClick={() => handleSubscribe(p.tier)}
                                        disabled={subscribingTier !== null}
                                    >
                                        {subscribingTier === p.tier ? 'Redirecting…' : `Switch to ${p.name}`}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {error && <div className="acctError">{error}</div>}
            </div>
        </div>
    );
}
